import { useState, useMemo, useEffect, useRef } from "react";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { normalizeOptions, filterOptionsBySearch } from "@/lib/dropdownUtils";

const SEARCH_KEYS = ["Escape", "ArrowUp", "ArrowDown", "Enter", " "];

const DropdownSearchInput = ({
  inputRef,
  value,
  onChange,
  onKeyDownKeys = SEARCH_KEYS,
}) => (
  <div
    className="px-3 py-2 sticky top-0 z-10 border-b"
    onClick={(e) => e.stopPropagation()}
    onMouseDown={(e) => {
      if (e.target.tagName !== "INPUT") e.preventDefault();
    }}
  >
    <div className="relative">
      <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
      <Input
        ref={inputRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Search..."
        className="pl-8 h-9 text-sm"
        onKeyDown={(e) => {
          if (onKeyDownKeys.includes(e.key)) e.stopPropagation();
        }}
        autoComplete="off"
      />
    </div>
  </div>
);

const CustomDropdown = ({
  label,
  options = [],
  value,
  onChange,
  placeholder = "Select an option",
  width = "w-[250px]",
  enableSearch = false,
  enableClear = true,
  isMandatory = false,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef(null);
  const debouncedSearch = useDebouncedValue(searchQuery, 300);

  const normalizedOptions = useMemo(
    () => normalizeOptions(options),
    [options]
  );

  const filteredOptions = useMemo(() => {
    if (!enableSearch || !debouncedSearch) return normalizedOptions;
    return filterOptionsBySearch(normalizedOptions, debouncedSearch);
  }, [debouncedSearch, normalizedOptions, enableSearch]);

  useEffect(() => {
    if (isOpen && enableSearch && inputRef.current) {
      const timer = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(timer);
    }
  }, [isOpen, enableSearch]);

  const handleValueChange = (val) => {
    onChange(val);
    setIsOpen(false);
  };

  const handleOpenChange = (open) => {
    setIsOpen(open);
    if (!open) setSearchQuery("");
  };

  return (
    <div className="space-y-2">
      {label && (
        <label className="text-sm font-medium">
          {label}
          {isMandatory && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <div className="relative">
        <Select
          value={value}
          onValueChange={handleValueChange}
          open={isOpen}
          onOpenChange={handleOpenChange}
        >
          <SelectTrigger className="bg-white pr-8">
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent className="bg-white">
            {enableSearch && (
              <DropdownSearchInput
                inputRef={inputRef}
                value={searchQuery}
                onChange={setSearchQuery}
              />
            )}

            <SelectGroup
              className={enableSearch ? "max-h-[200px] overflow-y-auto" : ""}
            >
              {filteredOptions.length > 0 ? (
                filteredOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))
              ) : (
                <div className="px-3 py-2 text-sm text-gray-500">
                  No results found
                </div>
              )}
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

export default CustomDropdown;
